import config from 'config';

import i18nService from '../i18nService';
import sessionActions from '../../actions/SessionActions';
import sessionConstants from '../../constants/SessionConstants';
import sessionReader from '../../store/readHelpers/SessionReader';
import storeManager from '../../store/StoreManager';
import utilService from '../UtilService';

import QuestionDataIdentifier from './QuestionDataIdentifier';

const defaultPrintParams = {
    show_pdf_panels: true,
};

function getRequestedLocale(javaLocale, languageId) {
    if (i18nService.isRomanianLanguageOverrideEnabled()) {
        return i18nService.getJavaLocaleFromLanguageId(16);
    }
    return javaLocale || i18nService.getJavaLocaleFromLanguageId(languageId) || 'en_us';
}

const sessionParameterService = {
    setSessionParams(params) {
        const {
            academyMarketplaceId,
            appReady = false,
            assessmentRunId,
            childAssessmentRunId,
            childContentRevisionId,
            childContext,
            contentItemId,
            contentRevisionId,
            context,
            dataSetType,
            debugSnapshots,
            debugTrackers,
            debugTimeZone,
            dispatchLogLevel,
            dynamicLoading,
            enableHypothesis,
            annotationSidebarDefaultExpanded,
            highlightContentItemId,
            locale,
            mode = sessionConstants.MODE_QUESTION_SET,
            partyId,
            printParams,
            layoutParams,
            requestingPartyId,
            annotationContext,
            selectedLanguageId,
            showAnnotatableResponses,
            viewAs,
            viewMedia = sessionConstants.VIEW_MEDIA_SCREEN,
        } = Object.assign({}, config.defaultSessionParams, params);

        const requestParameterIds = {};
        requestParameterIds.assessmentRunId = utilService.getIntOr(assessmentRunId);
        requestParameterIds.childAssessmentRunId = utilService.getIntOr(childAssessmentRunId);
        requestParameterIds.childContentRevisionId = utilService.getIntOr(childContentRevisionId);
        requestParameterIds.childContext = utilService.getIntOr(childContext);
        requestParameterIds.academyMarketplaceId = utilService.getIntOr(academyMarketplaceId);
        requestParameterIds.contentItemId = utilService.getIntOr(contentItemId);
        requestParameterIds.contentRevisionId = utilService.getIntOr(contentRevisionId);
        requestParameterIds.partyId = utilService.getIntOr(partyId);
        requestParameterIds.locale = locale;
        requestParameterIds.languageId = utilService.getIntOr(selectedLanguageId);
        requestParameterIds.highlightContentItemId = utilService.getIntOr(highlightContentItemId);
        requestParameterIds.requestingPartyId = utilService.getIntOr(requestingPartyId);

        const debugParams = Object.assign({}, config.debug, {
            dispatchLogLevel: utilService.getIntOr(dispatchLogLevel, 0),
            debugSnapshots: utilService.toBoolean(debugSnapshots),
            debugTrackers: utilService.toBoolean(debugTrackers),
            debugTimeZone: utilService.toBoolean(debugTimeZone),
        });

        const parameters = { appReady, context, dataSetType, debugParams, mode, requestParameterIds, viewAs, viewMedia };
        parameters.language = i18nService.getIetfLanguageCode(
            getRequestedLocale(locale, utilService.getIntOr(selectedLanguageId))
        );
        parameters.dynamicLoading = utilService.toBoolean(dynamicLoading);
        parameters.enableHypothesis = utilService.toBoolean(enableHypothesis);
        parameters.showAnnotatableResponses = utilService.toOptionalBoolean(showAnnotatableResponses);
        parameters.annotationSidebarDefaultExpanded = utilService.toBoolean(annotationSidebarDefaultExpanded);
        parameters.annotationContext = annotationContext;

        if (printParams) {
            try {
                if (typeof printParams === 'string') {
                    parameters.printParams = { ...defaultPrintParams, ...JSON.parse(printParams) };
                } else if (typeof printParams === 'object') {
                    parameters.printParams = { ...defaultPrintParams, ...printParams };
                }
            } catch (error) {
                window.console.error(error); // TrackJS is not initialised at this stage.
            }
        }

        if (layoutParams) {
            try {
                if (typeof layoutParams === 'string') {
                    parameters.layoutParams = JSON.parse(layoutParams);
                } else if (typeof layoutParams === 'object') {
                    parameters.layoutParams = layoutParams;
                }
            } catch (error) {
                window.console.error(error); // TrackJS is not initialised at this stage.
            }
        }

        parameters.dataSetType = sessionReader.getDataSetType(parameters);
        parameters.questionSetIdentifier = new QuestionDataIdentifier(parameters.dataSetType, requestParameterIds);

        storeManager.dispatch(
            sessionActions.replaceAll(parameters)
        );
    },
};

export default sessionParameterService;
